import { CommonModule } from '@angular/common'
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { AuthService } from '@app/modules/auth/services/auth.service'
import {
  NotificationChannel,
  isEmailOnlyNotificationChannel,
  notificationChannelDisplayName,
  notificationChannelsForForm
} from '@app/modules/database/models/resend-confirmation-pricing'
import { BulkProgressService } from '@app/modules/database/services/bulk-progress.service'
import { DatabaseService } from '@app/modules/database/services/database.service'
import { ClientRepresentedEventTiming, EventParser, ServerRepresentedEvent, ServerRepresentedEventTiming } from '@app/modules/events/models/event.model'
import { EventService } from '@app/modules/events/services/event.service'
import { BillingService, roundMoney } from '@app/modules/payments/services/billing.service'
import { PersonaTypes } from '@app/modules/personas/enums/persona-types.enum'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { InvitationTrackerService } from '@app/v2/event/services/invitation-tracker.service'
import { CheckinLocationDetails } from '@app/v2/event/types/event.type'
import { InvitationEmailTemplateTiming } from '@app/v2/event/types/invitation-email-template.type'
import { InvitationCollectionAddResult } from '@app/v2/event/types/invitation-tracker.type'
import { buildAddSkippedRows, getAddedEntriesCount, withDefaultAddResult } from '@app/v2/event/utils/invitation-collection-add.util'
import { BillingPaymentMethod } from '@app/v2/settings/components/billing-payments/billing/payment-cards-modal/payment-cards-modal.component'
import { WalletTopUpModalComponent } from '@app/v2/settings/components/billing-payments/billing/wallet-top-up-modal/wallet-top-up-modal.component'
import { OrganizationService } from '@app/v2/settings/services/organization.service'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { NavbarService } from '@app/v2/shared/components/navbar/navbar.service'
import { ProgressCircleComponent } from '@app/v2/shared/components/progress-circle/progress-circle.component'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { listenLayeredOverlayDismiss } from '@app/v2/shared/helpers/layered-overlay-dismiss'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { InvitationInfo, InvitationState, buildInvitationInfo } from '@invite/models/invitation-info.interface'
import { InviteService } from '@invite/services/invite.service'
import { Subject, of, timer } from 'rxjs'
import { catchError, debounceTime, distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators'
import { EventSelectionComponent } from '../event-selection/event-selection.component'
import { PersonaSelectionComponent } from '../persona-selection/persona-selection.component'

const COMPLETE_STEP_HEADER_ICON = `<svg class="tw-w-4 tw-h-4 tw-shrink-0 tw-block" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g clip-path="url(#invite_complete_check_clip)"><path d="M8 0C6.41775 0 4.87103 0.469192 3.55544 1.34824C2.23985 2.22729 1.21447 3.47672 0.608967 4.93853C0.00346627 6.40034 -0.15496 8.00887 0.153721 9.56072C0.462403 11.1126 1.22433 12.538 2.34315 13.6569C3.46197 14.7757 4.88743 15.5376 6.43928 15.8463C7.99113 16.155 9.59966 15.9965 11.0615 15.391C12.5233 14.7855 13.7727 13.7602 14.6518 12.4446C15.5308 11.129 16 9.58225 16 8C15.9977 5.87898 15.1541 3.8455 13.6543 2.34572C12.1545 0.845932 10.121 0.00232928 8 0ZM10.9656 6.9656L7.7656 10.1656C7.61558 10.3156 7.41213 10.3998 7.2 10.3998C6.98787 10.3998 6.78442 10.3156 6.6344 10.1656L5.0344 8.5656C4.88868 8.41472 4.80804 8.21263 4.80986 8.00288C4.81169 7.79312 4.89582 7.59247 5.04415 7.44414C5.19247 7.29582 5.39312 7.21168 5.60288 7.20986C5.81264 7.20804 6.01472 7.28867 6.1656 7.4344L7.2 8.4688L9.8344 5.8344C9.98528 5.68867 10.1874 5.60804 10.3971 5.60986C10.6069 5.61168 10.8075 5.69582 10.9559 5.84414C11.1042 5.99247 11.1883 6.19312 11.1901 6.40288C11.192 6.61263 11.1113 6.81472 10.9656 6.9656Z" fill="#111928"/></g><defs><clipPath id="invite_complete_check_clip"><rect width="16" height="16" fill="white"/></clipPath></defs></svg>`

const COMPLETE_STEP_BLOCKED_HEADER_ICON = `<svg class="tw-w-4 tw-h-4 tw-shrink-0 tw-block" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g clip-path="url(#invite_complete_info_clip)"><path d="M8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0ZM8.8 11.2C8.8 11.6418 8.44183 12 8 12C7.55817 12 7.2 11.6418 7.2 11.2V7.2C7.2 6.75817 7.55817 6.4 8 6.4C8.44183 6.4 8.8 6.75817 8.8 7.2V11.2ZM8 5.2C7.44772 5.2 7 4.75228 7 4.2C7 3.64772 7.44772 3.2 8 3.2C8.55228 3.2 9 3.64772 9 4.2C9 4.75228 8.55228 5.2 8 5.2Z" fill="#111928"/></g><defs><clipPath id="invite_complete_info_clip"><rect width="16" height="16" fill="white"/></clipPath></defs></svg>`

/**
 * Main registrant invitation component
 */
@Component({
  selector: 'ngx-invitation',
  standalone: true,
  templateUrl: './invitation.component.html',
  styleUrls: ['./invitation.component.scss'],
  providers: [InvitationTrackerService],
  imports: [
    CommonModule,
    FormsModule,
    ModalComponent,
    ProgressCircleComponent,
    EventSelectionComponent,
    PersonaSelectionComponent,
    WalletTopUpModalComponent
  ]
})
export class InvitationComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() source: 'PAST_EVENT' | 'NEW_EVENT' = 'PAST_EVENT'
  @Input() invitationInfo: InvitationInfo
  @Input() modalId = 'invite-to-event-modal'
  @Input() paidEvents: Partial<ServerRepresentedEvent>[] = []
  @Output() closed = new EventEmitter<void>()

  /** Same labels as notification-channel-picker / resend flow. */
  readonly channelLabel = notificationChannelDisplayName

  private _destroy$ = new Subject()
  private _searchTermChanged$: Subject<string> = new Subject<string>()
  private _currentPage = 1
  private _closedEmitted = false

  public optOutStatus = { opt_out: 0, notOptOut: 0 }

  public step = 0

  public InvitationState = InvitationState

  /**
   * Event invitation state
   */
  public state: InvitationState = InvitationState.TO_EVENT

  /** Cached workflow step used by the template (avoids repeated WORKFLOW lookups). */
  workflowState: InvitationState = InvitationState.TO_EVENT
  modalHeaderTitle = 'Send Invitations'
  modalHeaderIcon = ''
  modalWidthClass = 'tw-w-[95vw] sm:tw-w-[576px] lg:tw-w-[800px]'
  modalHeightClass = 'tw-h-[800px] tw-max-h-[80vh]'
  showEventSearch = false
  showOptOutSummary = false
  isMessageStep = false
  isToPersonaStep = false
  isFromEventStep = false
  isToEventStep = false
  isFromPersonaStep = false
  isChannelStep = false
  isCompleteStep = false
  addToCollectionResult: InvitationCollectionAddResult | null = null

  /**
   * Entries actually being added.
   * net added is eligible minus skipped.
   */
  get addedEntriesCount(): number {
    return getAddedEntriesCount(this.addToCollectionResult)
  }

  /** True when add-to-collection added nobody (after-send empty). */
  get isCompleteStepEmpty(): boolean {
    return this.addedEntriesCount === 0
  }

  /**
   * Summary rows for the skipped entries (avoids repeated template expressions).
   * @returns Summary rows for the skipped entries.
   */
  get skippedSummaryRows(): { count: number; label: string }[] {
    return buildAddSkippedRows(this.addToCollectionResult)
  }

  /** Search stays available when filtering; hidden when there are no eligible events at all. */
  get shouldShowEventSearch(): boolean {
    return this.showEventSearch && (this.events.length > 0 || !!this.searchTerm)
  }

  /** Empty state for invite event picker when the workspace has no live/upcoming events. */
  get showNoEligibleEventsMessage(): boolean {
    return (this.isToEventStep || this.isFromEventStep) && !this.processing && this.events.length === 0 && !this.searchTerm
  }

  public events: Partial<ServerRepresentedEvent>[] = []

  public event: Partial<ServerRepresentedEvent>

  public personas: Partial<Persona>[] = []

  public processing = true

  public searchTerm: string = ''

  public eventTimings: ClientRepresentedEventTiming[] = []

  public personasList: any

  public selectedFromPersonaSlug: string

  public selectedToPersonaSlug: string

  private unlistenLayeredOverlayDismiss?: () => void

  /** Full personas for the invite-from event (NEW_EVENT); PAST_EVENT uses DatabaseService.forms. */
  private _sourceEventForms: Persona[] = []
  private _addToCollectionInFlight = false

  /**
   * Invitation channel cards. SMS / WhatsApp are omitted when the source form has no phone-number element.
   * @returns Channels to show on the channel step
   */
  get inviteChannels(): NotificationChannel[] {
    return notificationChannelsForForm([NotificationChannel.EMAIL, NotificationChannel.WHATSAPP], this._sourceInviteForm())
  }

  /** Wallet check before Send / Send test (same pattern as resend-confirmation-flow). */
  loadingBalance = false
  /** Which action is waiting on balance (for button labels). */
  balanceCheckKind: null | 'invite' | 'test' = null
  insufficientBalance = false
  balanceChecked = false
  requiredAmountUsd = 0
  currentBalanceUsd = 0
  loadingPaymentMethods = false
  paymentMethods: BillingPaymentMethod[] = []
  /** Inline wallet top-up step on the message screen. */
  flowStep: 'message' | 'topup' = 'message'
  readonly Channel = NotificationChannel

  /**
   * Workflow for the invitation component
   */
  public readonly WORKFLOW = {
    NEW_EVENT: [
      // Select target Persona step
      {
        state: InvitationState.TO_PERSONA,
        title: 'Select target persona'
      },
      // Select source Event step
      {
        state: InvitationState.FROM_EVENT,
        title: 'Select source event'
      },
      // Select source Persona step
      {
        state: InvitationState.FROM_PERSONA,
        title: 'Select source persona'
      },
      {
        state: InvitationState.COMPLETE,
        title: 'Added to collection'
      }
    ],
    PAST_EVENT: [
      // Select target Event step
      {
        state: InvitationState.TO_EVENT,
        title: 'Select target event'
      },
      // Select target Persona step
      {
        state: InvitationState.TO_PERSONA,
        title: 'Select target persona'
      },
      // Channel + message invite flow temporarily disabled — add-to-collection runs after persona selection.
      // {
      //   state: InvitationState.CHANNEL,
      //   title: 'Select invitation channel'
      // },
      // TODO: handle the email template in future sprints
      // {
      //   state: InvitationState.MESSAGE,
      //   title: 'Customize your invitation'
      // },
      // Final step
      {
        state: InvitationState.COMPLETE,
        title: 'Added to collection'
      }
    ]
  }

  constructor(
    private service: InviteService,
    private eventService: EventService,
    public authService: AuthService,
    private router: Router,
    private databaseService: DatabaseService,
    private bulkProgressService: BulkProgressService,
    private navbarService: NavbarService,
    private billingService: BillingService,
    private _organizationService: OrganizationService,
    private _modalService: ModalService,
    private _cdr: ChangeDetectorRef,
    private _alertService: AlertService,
    private _invitationTrackerService: InvitationTrackerService,
    private _wizardService: WizardService
  ) {}

  ngOnInit(): void {
    // Initialize invitationInfo with default values if not provided as input
    if (!this.invitationInfo) {
      this.invitationInfo = buildInvitationInfo({ selectionCount: 0 })
    }

    this._initializeEventAndStep()
    this.loadEvents(false)
    this._searchTermChanged$
      .pipe(debounceTime(500), distinctUntilChanged())
      .pipe(takeUntil(this._destroy$))
      .subscribe((term) => {
        this.searchTerm = term
        this.loadEvents(false)
      })
    this.updateOptOutStatus(this.invitationInfo.ids)

    this.unlistenLayeredOverlayDismiss = listenLayeredOverlayDismiss({
      closeParent: () => this.closeModal()
    })
    this._syncModalUiForStep()
  }

  ngAfterViewInit(): void {
    // Open after the modal view exists so @if (isModalOpen()) is not stuck false until a click.
    if (!this._modalService.isModalOpen(this.modalId)) {
      this._modalService.toggleModal(this.modalId)
    }
    this._cdr.detectChanges()
  }

  /**
   * Closes the invitation modal and notifies the parent.
   * Safe if the modal chrome already closed via X / backdrop / Escape.
   */
  closeModal(): void {
    if (this._modalService.isModalOpen(this.modalId)) {
      this._modalService.toggleModal(this.modalId)
    }
    this._emitClosed()
  }

  /**
   * Handles close from the modal X button or backdrop (ModalComponent already toggled).
   */
  onModalClose(): void {
    this._emitClosed()
  }

  private _emitClosed(): void {
    if (this._closedEmitted) return
    this._closedEmitted = true
    this.closed.emit()
  }

  ngOnDestroy() {
    this.unlistenLayeredOverlayDismiss?.()
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Emits the new search term
   * @param $event string
   */
  onSearchTermChangedEvent($event) {
    this._searchTermChanged$.next($event)
  }

  /**
   * Initializes the event and step of the invitation workflow for new event source
   */
  private _initializeEventAndStep() {
    if (this.source === 'PAST_EVENT') return
    this.invitationInfo.action = 'all'
    const currentEvent = this.databaseService.currentEvent
    currentEvent.forms = this.databaseService.forms.value.map((form) => ({
      id: form.id,
      name: form.name,
      slug: form.slug,
      weight: form.weight,
      enable_payment: form.enable_payment,
      products_count: form.products_count,
      type: form.type,
      parent: form.parent,
      registration_amount: form.registration_amount,
      currency: form.currency
    }))
    currentEvent.public_start_date = currentEvent.public_start_date
    currentEvent.public_end_date = currentEvent.public_end_date
    this.setEvent(currentEvent)
    this.step--
    this._syncModalUiForStep()
  }

  /**
   * Loads all elligable events for the current workspace
   * @param appendToEventList boolean
   */
  loadEvents(appendToEventList: boolean = true) {
    this.processing = true
    if (!this.invitationInfo.isInvitationModal) {
      this.events = this.paidEvents
      this.processing = false
      return
    }
    if (this.source === 'NEW_EVENT') {
      if (!appendToEventList) this._currentPage = 1
      this.eventService
        .getEvents(this._currentPage, this.searchTerm, 0)
        .pipe(
          map((events: { data: ServerRepresentedEvent[] }) => this._formatEventList(events.data)),
          takeUntil(this._destroy$)
        )
        .subscribe((result: Partial<ServerRepresentedEvent>[]) => {
          this.events = !appendToEventList ? result : [...this.events, ...result]
          this.processing = false
          this._cdr.detectChanges()
        })
      return
    }
    this.eventService
      .getEventsExcept(1, this.searchTerm)
      .pipe(
        map((events: ServerRepresentedEvent[]) => this._formatEventList(events)),
        takeUntil(this._destroy$)
      )
      .subscribe((result: any) => {
        this.events = result
        this.processing = false
        // Projected modal body is owned by this component; ModalComponent.detectChanges()
        // does not refresh the spinner/events list — force CD here.
        this._cdr.detectChanges()
      })
  }

  /**
   * Formats the event list passed to the event selector component
   * @param events ServerRepresentedEvent[]
   * @returns Partial<ServerRepresentedEvent>[]
   */
  private _formatEventList(events: ServerRepresentedEvent[]): Partial<ServerRepresentedEvent>[] {
    return events.map((event: ServerRepresentedEvent) => {
      return {
        name: event.name,
        slug: event.slug,
        logo: event.logo,
        cover_page: event.cover_page,
        status: event.status,
        social_media: event.social_media,
        id: event.id,
        checkins: event.checkins,
        location_details: event.location_details,
        forms: event.forms
          .map((form) => {
            return {
              name: form.name,
              slug: form.slug,
              weight: form.weight,
              enable_payment: form.enable_payment,
              products_count: form.products_count,
              type: form.type,
              registration_amount: form.registration_amount,
              currency: form.currency
            }
          })
          .sort((a, b) => a.weight - b.weight),
        public_start_date: event.public_start_date,
        public_end_date: event.public_end_date
      }
    })
  }

  /**
   * Gets event timings for the selected event
   * @param event ServerRepresentedEvent
   */
  getEventTimings(event: ServerRepresentedEvent) {
    this.eventService
      .getEventTiming(event.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((timeResult: ServerRepresentedEventTiming[]) => {
        const eventInfo = EventParser.toResponse(EventParser.compose(event, timeResult))
        this.eventTimings = eventInfo.event_timing
      })
  }

  /**
   * Sets the current event and gets it's timings, updates the personas list and invitation object
   * @param event Event
   */
  setEvent(event: ServerRepresentedEvent) {
    if (!this.invitationInfo.isInvitationModal) {
      this.closeModal()
      this.router.navigate([`/wizard/update/${event.id}`])
      return
    }
    this.processing = true
    this.getEventTimings(event)
    const slugKey = this.WORKFLOW[this.source][this.step].state === InvitationState.TO_EVENT || !this.step ? 'eventSlug' : 'fromEventSlug'
    this.setPersonas(event.forms, this._counterpartPersonaSlug(event.slug, slugKey))
    // The event object should represent the target event
    this.event = slugKey === 'eventSlug' ? event : this.event
    // Updates either the target event slug or the source event slug
    this.invitationInfo[slugKey] = event.slug
    if (slugKey === 'fromEventSlug' && event.id) {
      this._loadSourceEventForms(event.id)
    }
    this.step++
    this.processing = false
    this._syncModalUiForStep()
  }

  /**
   * Persona on the other side of the invite when the same event is picked for both source and target.
   * Hidden from the persona list so entries are not invited into their own persona.
   * @param eventSlug Slug of the event being selected
   * @param slugKey Whether the selected event is the target or the source
   * @returns Persona slug to exclude, if any
   */
  private _counterpartPersonaSlug(eventSlug: string, slugKey: 'eventSlug' | 'fromEventSlug'): string | undefined {
    if (slugKey === 'eventSlug') {
      return eventSlug === this.invitationInfo.fromEventSlug ? this.invitationInfo.fromFormSlug : undefined
    }
    return eventSlug === this.invitationInfo.eventSlug ? this.invitationInfo.formSlug : undefined
  }

  /**
   * Loads full source-event personas so WhatsApp can be gated on a phone-number form element.
   * @param eventId Source event id
   */
  private _loadSourceEventForms(eventId: number): void {
    this.eventService
      .getEventForms(eventId)
      .pipe(
        catchError(() => of([] as Persona[])),
        takeUntil(this._destroy$)
      )
      .subscribe((forms) => {
        this._sourceEventForms = forms ?? []
        this.skipChannelStepIfEmailOnly()
      })
  }

  /**
   * Persona invitees are sent from. Phone-channel cards depend on this form's form_elements.
   * @returns Source persona, or undefined if it is not loaded yet
   */
  private _sourceInviteForm(): Persona | undefined {
    const slug = this.invitationInfo?.fromFormSlug
    if (!slug) return undefined
    if (this.source === 'NEW_EVENT') {
      return this._sourceEventForms.find((form) => form.slug === slug)
    }
    return this.databaseService.forms?.value?.find((form: Persona) => form.slug === slug)
  }

  /**
   * Whether the source persona is known enough to decide if WhatsApp should be offered.
   * NEW_EVENT waits until the source event's forms have been fetched.
   * @returns true when channel filtering can run
   */
  private _canResolveSourceInviteForm(): boolean {
    if (this.source === 'NEW_EVENT') return this._sourceEventForms.length > 0
    return true
  }

  /**
   * Skip the channel picker when Email is the only remaining option and advance to the message step.
   */
  private skipChannelStepIfEmailOnly(): void {
    if (this.WORKFLOW[this.source][this.step]?.state !== InvitationState.CHANNEL) return
    if (!this._canResolveSourceInviteForm()) return
    if (!isEmailOnlyNotificationChannel(this.inviteChannels)) return
    this.selectInviteChannel(NotificationChannel.EMAIL)
  }

  /**
   * Sets the selectable personas list, excluding generics and group-form parents/members.
   * Members reference a parent id (e.g. parent: 17425); that parent persona is excluded too.
   * @param personas Persona[]
   * @param excludedPersonaSlug Persona slug to hide (e.g. the source persona when targeting the same event)
   */
  setPersonas(personas: Partial<Persona>[] = [], excludedPersonaSlug?: string) {
    const groupParentIds = new Set(personas.filter((persona) => persona.parent != null).map((persona) => persona.parent as number))

    this.personasList = personas.filter((persona) => {
      if (excludedPersonaSlug && persona.slug === excludedPersonaSlug) return false
      if (persona.type === PersonaTypes.MEMBER || persona.type === PersonaTypes.GROUP) return false
      if (persona.parent != null) return false
      if (persona.id != null && groupParentIds.has(persona.id)) return false
      return true
    })
  }

  /**
   * sets the selected persona
   * @param personaSlug string
   */
  setPersona(personaSlug: string) {
    const currentState = this.WORKFLOW[this.source][this.step].state
    const slugKey = currentState === InvitationState.TO_PERSONA ? 'formSlug' : 'fromFormSlug'
    // Updates either the target persona slug or the source persona slug
    this.invitationInfo[slugKey] = personaSlug
    if (slugKey === 'formSlug') this.selectedToPersonaSlug = personaSlug
    else {
      this.selectedFromPersonaSlug = personaSlug
      // Check the opt out status after selecting the source persona
      this.updateOptOutStatus([])
    }

    // After the persona that completes target selection, add to invitation collection
    const shouldAddToCollection =
      (this.source === 'PAST_EVENT' && currentState === InvitationState.TO_PERSONA) || (this.source === 'NEW_EVENT' && currentState === InvitationState.FROM_PERSONA)

    if (shouldAddToCollection) {
      this._submitAddToInvitationCollection()
      return
    }

    this.step++
    this._syncModalUiForStep()
  }

  /**
   * Adds the current selection into the target persona invitation collection, then shows the complete step.
   */
  private _submitAddToInvitationCollection(): void {
    if (this._addToCollectionInFlight) return

    const organizationSlug = this.authService.currentWorkspace?.slug
    const sourceEventSlug = this.invitationInfo.fromEventSlug
    const sourcePersonaSlug = this.invitationInfo.fromFormSlug
    const targetEvent = this.invitationInfo.eventSlug
    const targetPersona = this.invitationInfo.formSlug

    if (!organizationSlug || !sourceEventSlug || !sourcePersonaSlug || !targetEvent || !targetPersona) {
      this._alertService.alert(Alert.ERROR, 'Cannot add to invitation collection. Missing event or persona details.')
      return
    }

    this._addToCollectionInFlight = true
    this.processing = true
    this._cdr.detectChanges()

    this._invitationTrackerService
      .addToInvitationCollection(
        { organizationSlug, eventSlug: sourceEventSlug, personaSlug: sourcePersonaSlug },
        {
          action: this.invitationInfo.action || 'all',
          ids: this.invitationInfo.ids ?? [],
          search: this.invitationInfo.search,
          parent_id: this.invitationInfo.action === 'exclude' ? this.invitationInfo.parent_id : undefined,
          target_event: targetEvent,
          target_persona: targetPersona
        }
      )
      .pipe(
        map((res) => withDefaultAddResult(res)),
        takeUntil(this._destroy$)
      )
      .subscribe({
        next: (result) => {
          this._addToCollectionInFlight = false
          if (!result) {
            this.processing = false
            this._cdr.detectChanges()
            this._alertAddToCollectionFailed()
            return
          }
          this.addToCollectionResult = result
          this.processing = false
          this._goToCompleteStep()
        },
        error: () => {
          this._addToCollectionInFlight = false
          this.processing = false
          this._cdr.detectChanges()
          this._alertAddToCollectionFailed()
        }
      })
  }

  private _alertAddToCollectionFailed(): void {
    this._alertService.alert(Alert.ERROR, 'Could not add to invitation collection. Please try again or contact support.')
  }

  private _alertWalletBalanceVerifyFailed(): void {
    this._alertService.alert(Alert.ERROR, 'Could not verify wallet balance. Please try again.')
  }

  /**
   * Jumps the workflow to the COMPLETE step and refreshes modal chrome.
   */
  private _goToCompleteStep(): void {
    const completeIndex = this.WORKFLOW[this.source].findIndex((step) => step.state === InvitationState.COMPLETE)
    this.step = completeIndex >= 0 ? completeIndex : this.WORKFLOW[this.source].length - 1
    this._syncModalUiForStep()
    this._cdr.detectChanges()
  }

  /**
   * Sends a single test via PATCH invite-test with the current user's email (email channel) or phone (SMS / WhatsApp).
   */
  inviteTest() {
    setTimeout(() => {
      const receiver = this.testInviteReceiver
      if (!receiver?.trim()) {
        const detail = this.inviteChannel === NotificationChannel.EMAIL ? 'Your account has no email address.' : 'Add a phone number in your profile to send a test.'
        this._alertService.alert(Alert.ERROR, `Cannot send test. ${detail}`)
        return
      }
      const trimmed = receiver.trim()
      if (this.inviteChannel === NotificationChannel.EMAIL) {
        this.executeInviteTest(trimmed)
        return
      }
      const testCostUsd = this.authService.calculateInvitationTotalPriceUsd(this.inviteChannel, 1)
      this.ensureBalanceThen(testCostUsd, () => this.executeInviteTest(trimmed), 'test')
    })
  }

  private executeInviteTest(receiver: string) {
    this.processing = true
    this.service
      .inviteTest(this.invitationInfo, receiver)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.processing = false
          this._alertService.alert(Alert.SUCCESS, `Test has been sent to ${receiver}`)
        },
        error: () => {
          this.processing = false
        }
      })
  }

  private ensureBalanceThen(requiredUsd: number, onOk: () => void, kind: 'invite' | 'test', onCancel?: () => void) {
    if (!this.authService.currentWorkspace?.id) {
      onCancel?.()
      return
    }

    this.loadingBalance = true
    this.balanceCheckKind = kind
    const workspaceId = this.authService.currentWorkspace.id.toString()
    this.navbarService
      .getBalance(workspaceId)
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe({
        next: (balance) => {
          this.loadingBalance = false
          this.balanceCheckKind = null
          if (balance < requiredUsd) {
            this._alertService.alert(Alert.ERROR, 'Insufficient wallet balance. Cannot send test')
            onCancel?.()
            return
          }
          onOk()
        },
        error: () => {
          this.loadingBalance = false
          this.balanceCheckKind = null
          this._alertWalletBalanceVerifyFailed()
          onCancel?.()
        }
      })
  }

  /**
   * Amount still needed in the wallet to cover the invitation total.
   */
  balanceShortfallUsd(): number {
    return Math.max(0, roundMoney(this.requiredAmountUsd - this.currentBalanceUsd))
  }

  /**
   * Suggested top-up for the wallet: shortfall floored at $50.
   */
  suggestedTopUpAmount(): number {
    return Math.max(50, this.balanceShortfallUsd())
  }

  /**
   * Fetch wallet balance and compare it to the invitation total on the message step.
   * @param {number} [optimisticBalanceUsd] Known balance to apply immediately (e.g. after top-up).
   */
  checkInviteWalletBalance(optimisticBalanceUsd?: number): void {
    const total = roundMoney(this.inviteTotalCostUsd)
    if (total <= 0 || !this.billingService.workspaceId) {
      this.loadingBalance = false
      this.insufficientBalance = false
      this.balanceChecked = true
      this.requiredAmountUsd = 0
      return
    }

    if (optimisticBalanceUsd !== undefined) {
      this._applyInviteBalanceCheck(optimisticBalanceUsd, total)
    } else {
      this.loadingBalance = true
      this.insufficientBalance = false
      this.balanceChecked = false
    }

    const workspaceId = String(this.billingService.workspaceId)
    this.navbarService
      .getBalance(workspaceId)
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe({
        next: (balance) => {
          const resolvedBalance = optimisticBalanceUsd !== undefined ? Math.max(balance, optimisticBalanceUsd) : balance
          this._applyInviteBalanceCheck(resolvedBalance, total)
        },
        error: () => {
          this.loadingBalance = false
          this.balanceChecked = false
          this._alertWalletBalanceVerifyFailed()
        }
      })
  }

  /**
   * Update balance state and whether the wallet can cover the invitation cost.
   * @param {number} balance Current wallet balance in USD
   * @param {number} total Required total in USD
   */
  private _applyInviteBalanceCheck(balance: number, total: number): void {
    this.loadingBalance = false
    this.balanceChecked = true
    this.currentBalanceUsd = roundMoney(balance)
    this.requiredAmountUsd = roundMoney(total)
    const shortfall = roundMoney(total - this.currentBalanceUsd)
    this.insufficientBalance = shortfall > 0
  }

  /**
   * Load payment methods and navigate to the inline top-up step.
   */
  goToTopUpStep(): void {
    if (this.loadingPaymentMethods) return

    this.loadingPaymentMethods = true
    this._organizationService
      .getOrganizationPaymentMethods(this.billingService.workspaceId)
      .pipe(
        catchError(() => of([] as BillingPaymentMethod[])),
        take(1),
        takeUntil(this._destroy$)
      )
      .subscribe((methods) => {
        this.paymentMethods = methods
        this.loadingPaymentMethods = false
        this.flowStep = 'topup'
        this._syncModalUiForStep()
      })
  }

  /**
   * Return from top-up to the message step and re-check wallet balance.
   */
  backFromTopUp(): void {
    this.flowStep = 'message'
    this._syncModalUiForStep()
    this.checkInviteWalletBalance()
  }

  /**
   * After a successful top-up, return to the message step and re-check wallet balance.
   */
  public onWalletTopUpSuccess(amount: number): void {
    if (!this.billingService.workspaceId) return

    const updatedBalance = roundMoney(this.currentBalanceUsd + amount)
    this.billingService.publishWalletBalance(updatedBalance)
    this.flowStep = 'message'
    this._syncModalUiForStep()
    this.checkInviteWalletBalance(updatedBalance)
  }

  /**
   * Modal width / height / title and step flags for the template.
   * Call after step, flowStep, or invite channel changes.
   */
  private _syncModalUiForStep(): void {
    const state = this.WORKFLOW[this.source][this.step]?.state ?? InvitationState.TO_EVENT
    this.workflowState = state
    this.isMessageStep = state === InvitationState.MESSAGE
    this.isToPersonaStep = state === InvitationState.TO_PERSONA
    this.isFromPersonaStep = state === InvitationState.FROM_PERSONA
    this.isToEventStep = state === InvitationState.TO_EVENT
    this.isFromEventStep = state === InvitationState.FROM_EVENT
    this.isChannelStep = state === InvitationState.CHANNEL
    this.isCompleteStep = state === InvitationState.COMPLETE

    this.showEventSearch = !!this.invitationInfo?.isInvitationModal && this.flowStep !== 'topup' && (state === InvitationState.TO_EVENT || state === InvitationState.FROM_EVENT)
    this.showOptOutSummary = !!this.invitationInfo?.isInvitationModal && this.flowStep === 'message' && state === InvitationState.MESSAGE

    if (!this.invitationInfo?.isInvitationModal) {
      this.modalHeaderTitle = "Changing workspace payment gateway can affect it's paid events"
      this.modalHeaderIcon = ''
    } else if (this.flowStep === 'topup') {
      this.modalHeaderTitle = 'Top up wallet'
      this.modalHeaderIcon = ''
    } else if (state === InvitationState.COMPLETE) {
      if (this.isCompleteStepEmpty) {
        this.modalHeaderTitle = "Entries can't be added"
        this.modalHeaderIcon = COMPLETE_STEP_BLOCKED_HEADER_ICON
      } else {
        this.modalHeaderTitle = 'Entries are being added'
        this.modalHeaderIcon = COMPLETE_STEP_HEADER_ICON
      }
    } else if (state === InvitationState.MESSAGE) {
      this.modalHeaderTitle = `Send invitations via ${notificationChannelDisplayName(this.inviteChannel)}`
      this.modalHeaderIcon = ''
    } else {
      this.modalHeaderTitle = this.WORKFLOW[this.source][this.step]?.title || 'Send Invitations'
      this.modalHeaderIcon = ''
    }

    if (this.flowStep === 'topup') {
      this.modalWidthClass = 'tw-w-11/12 tw-max-w-xl'
    } else if (state === InvitationState.COMPLETE) {
      this.modalWidthClass = 'tw-w-[95vw] sm:tw-w-[600px]'
    } else if (state === InvitationState.CHANNEL) {
      this.modalWidthClass = 'tw-w-[95vw] sm:tw-w-[576px] lg:tw-w-[640px]'
    } else {
      this.modalWidthClass = 'tw-w-[95vw] sm:tw-w-[576px] lg:tw-w-[800px]'
    }

    if (state === InvitationState.COMPLETE || state === InvitationState.CHANNEL || this.flowStep === 'topup') {
      this.modalHeightClass = 'tw-h-fit tw-max-h-[90vh]'
    } else {
      this.modalHeightClass = 'tw-h-[800px] tw-max-h-[80vh]'
    }
  }

  /**
   * Closes the invite modal and opens Invitation Tracker for the target event/persona.
   */
  async viewInvitationCollection(): Promise<void> {
    const targetEvent = this._resolveTargetEventForCollection()
    if (!targetEvent?.id) {
      this._alertService.alert(Alert.ERROR, 'Could not open invitation collection. Missing target event details.')
      return
    }

    const { InvitationTrackerComponent } = await import('@app/v2/event/components/invitation-tracker/invitation-tracker.component')
    this.closeModal()
    this._wizardService.open({
      component: InvitationTrackerComponent,
      componentInputs: {
        eventId: targetEvent.id,
        eventName: targetEvent.name ?? '',
        eventSlug: targetEvent.slug ?? this.invitationInfo.eventSlug,
        initialPersonaSlug: this.invitationInfo.formSlug || '',
        emailTemplateEvent: {
          name: targetEvent.name,
          slug: targetEvent.slug,
          public_start_date: targetEvent.public_start_date,
          public_end_date: targetEvent.public_end_date,
          location_details: this._resolveEventLocationDetails(targetEvent)
        },
        emailTemplateTimings: this.eventTimings.map(
          (timing): InvitationEmailTemplateTiming => ({
            day: timing.day,
            start_datetime: timing.start_datetime ?? null,
            end_datetime: timing.end_datetime ?? null,
            info: timing.info ?? null
          })
        )
      },
      headerConfig: {
        breadcrumbs: [targetEvent.name || 'Event', 'Invitation Tracking']
      }
    })
  }

  /**
   * Target event for “View collection” — selected invite-to event, or current DB event for NEW_EVENT.
   */
  private _resolveTargetEventForCollection(): Partial<ServerRepresentedEvent> | undefined {
    if (this.event?.id) return this.event
    const current = this.databaseService.currentEvent
    if (current?.id && (!this.invitationInfo.eventSlug || current.slug === this.invitationInfo.eventSlug)) {
      return current
    }
    return undefined
  }

  /**
   * Picks location details for the email template map from event / zone / checkin fields.
   */
  private _resolveEventLocationDetails(event: Partial<ServerRepresentedEvent>): CheckinLocationDetails | null {
    const anyEvent = event as Partial<ServerRepresentedEvent> & {
      location_details?: CheckinLocationDetails
      zones?: { physical?: { location_details?: CheckinLocationDetails } }
      checkins?: Array<{ location_details?: CheckinLocationDetails }>
    }

    return anyEvent.location_details ?? anyEvent.zones?.physical?.location_details ?? anyEvent.checkins?.[0]?.location_details ?? null
  }

  goToBilling() {
    this.authService.navigateToPage('billing')
    this.closeModal()
  }

  /** Email or phone used for “Send a test”, matching the selected channel. */
  get testInviteReceiver(): string {
    switch (this.inviteChannel) {
      case NotificationChannel.EMAIL:
        return this.authService.user?.email ?? ''
      case NotificationChannel.SMS:
      case NotificationChannel.WHATSAPP:
        return this.authService.user?.phone_number ?? ''
    }
  }

  /**
   * Navigates to a previous state based on the current state.
   * Skips the channel picker when Email is the only option.
   */
  back() {
    if (this.flowStep === 'topup') {
      this.backFromTopUp()
      return
    }
    this.step--
    if (this.WORKFLOW[this.source][this.step]?.state === InvitationState.CHANNEL && this._canResolveSourceInviteForm() && isEmailOnlyNotificationChannel(this.inviteChannels)) {
      this.step--
    }
    this._syncModalUiForStep()
  }

  /** Active delivery channel for pricing and header. */
  get inviteChannel(): NotificationChannel {
    return this.invitationInfo.inviteChannel ?? NotificationChannel.EMAIL
  }

  get inviteRecipientCount(): number {
    const notOptOut = this.optOutStatus.notOptOut
    const selectionCount = this.invitationInfo?.selectionCount
    // Filtered select-all: opt-out API without `search` can return the full form total.
    // Never price above the filtered selection count from the database view.
    if (typeof selectionCount === 'number' && selectionCount > 0 && notOptOut > selectionCount) {
      return selectionCount
    }
    return notOptOut
  }

  /** Opted-out count for header; 0 when opt-out response exceeded filtered selection (unscoped API). */
  get inviteOptOutCount(): number {
    const notOptOut = this.optOutStatus.notOptOut
    const selectionCount = this.invitationInfo?.selectionCount
    if (typeof selectionCount === 'number' && selectionCount > 0 && notOptOut > selectionCount) {
      return 0
    }
    return this.optOutStatus.opt_out
  }

  get inviteTotalCostUsd(): number {
    return this.authService.calculateInvitationTotalPriceUsd(this.inviteChannel, this.inviteRecipientCount)
  }

  unitPriceUsd(ch: NotificationChannel): number {
    return this.authService.invitationUnitPriceUsd[ch]
  }

  /**
   * Primary send-invitations CTA label (balance check, top-up, send).
   * @returns {string} Copy for the current button state
   */
  sendInvitationsButtonLabel(): string {
    let state: 'checking' | 'loading' | 'topUp' | 'send' = 'send'
    if (this.loadingBalance && this.balanceCheckKind === 'invite') state = 'checking'
    else if (this.loadingPaymentMethods) state = 'loading'
    else if (this.insufficientBalance) state = 'topUp'

    switch (state) {
      case 'checking':
        return 'Checking balance…'
      case 'loading':
        return 'Loading…'
      case 'topUp':
        return 'Top up'
      case 'send':
        return 'Send invitations'
    }
  }

  /**
   * Whether send invitations can proceed (balance OK when cost applies).
   */
  canSendInvitationsProceed(): boolean {
    if (this.inviteTotalCostUsd <= 0) return true
    if (this.insufficientBalance) return true
    return this.balanceChecked
  }

  /**
   * After choosing persona, user picks a channel; email advances immediately to templates.
   */
  selectInviteChannel(ch: NotificationChannel) {
    this.invitationInfo.inviteChannel = ch
    this.step++
    this.flowStep = 'message'
    this._syncModalUiForStep()
    this.checkInviteWalletBalance()
  }

  updateOptOutStatus(ids: string[]) {
    if (this.source === 'NEW_EVENT' && !this.step) return
    this.eventService
      .getOptOutStatus(this.authService.currentWorkspace.slug, this.invitationInfo, ids)
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        this.optOutStatus = response
        if (this.WORKFLOW[this.source][this.step]?.state === InvitationState.MESSAGE && this.flowStep === 'message') {
          this.checkInviteWalletBalance()
        }
      })
  }

  /**
   * Loads the next page of events
   * @param $page number
   */
  nextPage($page) {
    this._currentPage = $page
    this.loadEvents()
  }
}
